import { useEffect, useState } from "react";
import { Clock, Save, CheckCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getUserSettings, updateUserSettings } from "../utils/api";

const IDLE_OPTIONS = [
  { value: 0,   label: "Off" },
  { value: 15,  label: "15 minutes" },
  { value: 30,  label: "30 minutes" },
  { value: 60,  label: "1 hour" },
  { value: 120, label: "2 hours" },
];

export default function Settings() {
  const { currentUser, emailVerified } = useAuth();
  const [idleMinutes, setIdleMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    (async () => {
      try {
        const s = await getUserSettings();
        setIdleMinutes(s?.idleTimeoutMinutes || 0);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await updateUserSettings({ idleTimeoutMinutes: idleMinutes });
      setSaved(true);
      window.dispatchEvent(new Event("idle-timeout-changed"));
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage account and app preferences</p>
      </div>

      {/* Account info */}
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Account</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Email</span>
            <span className="text-gray-900 dark:text-gray-100 truncate ml-3">{currentUser?.email}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-500 dark:text-gray-400">Verified</span>
            {emailVerified ? (
              <span className="inline-flex items-center gap-1 text-secondary-600 dark:text-secondary-400 font-medium">
                <CheckCircle className="w-4 h-4" /> Verified
              </span>
            ) : (
              <span className="text-yellow-700 dark:text-yellow-400 font-medium">Not verified</span>
            )}
          </div>
        </div>
      </div>

      {/* Security */}
      <form onSubmit={handleSave} className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Auto sign-out after inactivity
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Signs you out automatically after a period of no mouse, keyboard, or touch activity.
          Useful on shared devices.
        </p>

        {loading ? (
          <div className="py-3 text-sm text-gray-400 dark:text-gray-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {IDLE_OPTIONS.map((o) => (
              <button
                type="button"
                key={o.value}
                onClick={() => setIdleMinutes(o.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  idleMinutes === o.value
                    ? "bg-primary-600 text-white border-primary-600"
                    : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <button type="submit" disabled={saving || loading} className="btn-primary">
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : "Save"}
          </button>
          {saved && (
            <span className="inline-flex items-center gap-1 text-sm text-secondary-600 dark:text-secondary-400">
              <CheckCircle className="w-4 h-4" /> Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
